import { Component, Injectable, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DocumentActor } from 'src/app/models/actors';
import { User } from 'src/app/models/user';
import { RequestStatuses, StatusEnum } from 'src/app/constants/status.enum';
import { RequestStatusService } from '../../requeststatus.service';
import { RequestStatusActionsEnum } from 'src/app/constants/RequestStatusActions.enum';
import { VerifyRequestActionService } from 'src/app/services/verify-request-action.service';
import { StatusRequestModel } from 'src/app/models/status/status-request.model';
import { RequestActionTypeEnum } from 'src/app/constants/request-action-type.enum';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { ActorSendGroupStatusInformationModel } from 'src/app/models/api/actor-status-information.model';
import { MatchContacts } from 'src/app/directives/match-contacts.function';

@Injectable()
export abstract class BaseStatusActions implements OnDestroy {
  private requestSubscription!: Subscription;
  private sendgroupSubscription!: Subscription;

  showMSplugins: boolean = false;
  actor!: DocumentActor;
  dossierPersonId!: string;
  RequestId!: string;
  user!: User;
  usersubscription: Subscription;
  statusIndicator!: string;
  timestamp!: string;
  translations: any = <any>{};
  requestType!: string;
  allowedToChangeOwnership: boolean = false;
  requestGuid!: string;
  request: StatusRequestModel = {} as StatusRequestModel;
  sendgroups: ActorSendGroupStatusInformationModel[] = [];

  requestStatusActionsEnum = RequestStatusActionsEnum;

  constructor(protected requestStatusService: RequestStatusService, protected authenticationService: AuthenticationService, protected verifyRequestActionService: VerifyRequestActionService) {
    this.usersubscription = this.authenticationService.currentUser.subscribe((data) => {
      this.user = data;
      this.showMSplugins = this.user.aMsPluginIsEnabled;
    });
    this.requestSubscription = this.requestStatusService.selectedRequest$.subscribe(request => {
      if (Object.keys(request).length === 0) return;
      this.request = request;
      this.requestGuid = request.id;
      this.allowedToChangeOwnership = this.requestStatusService.isUserAllowedToChangeOwnership();
    });
    this.sendgroupSubscription = this.requestStatusService.sendgroups$.subscribe(sendgroups => {
      this.sendgroups = sendgroups;
    });
  }

  allowedToDoAction() {
    return this.exportAllowed() || this.withdrawAllowed() || this.purgeAllowed() || this.restoreAllowed() || this.deleteAllowed() || this.becomeOwnerAllowed();
  }

  ngOnDestroy(): void {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }
    if (this.sendgroupSubscription) {
      this.sendgroupSubscription.unsubscribe();
    }
  }

  editReqeustAllowed() {
    // Whether you are allowed
    return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.EditRequest);
  }

  downloadAllowed() {
    // Whether you are allowed
    return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Download) ||
      // or completed/purged requests can also be downloaded by "anyone"
      this.request.currentStatus == StatusEnum.Completed ||
      this.request.currentStatus == StatusEnum.Purged;
  }
  becomeOwnerAllowed() {
    // no status is checked for ownership transfer, just whether you are allowed
    return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.BecomeOwner);
  }
  exportAllowed() {
    // no status is checked for export, just whether you are allowed
    return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Export);
  }
  purgeAllowed() {
    let purgeAllowedStatuses: string[] = RequestStatuses.ThatCanBe_Purged;
    if (purgeAllowedStatuses.includes(this.request.currentStatus)) {
      // /\ above, checks if REQUEST allows action
      // \/ below, checks if YOU are allowed
      return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Purge);
    }
    return false;
  }

  deleteAllowed() {
    let deleteAllowedStatuses: string[] = RequestStatuses.ThatCanBeDeleted;
    if (deleteAllowedStatuses.includes(this.request.currentStatus)) {
      // /\ above, checks if REQUEST allows action
      // \/ below, checks if YOU are allowed
      return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Delete);
    }
    return false;
  }

  withdrawAllowed() {
    let withdrawAllowedStatuses: string[] = RequestStatuses.ThatCanBeWithdrawn;
    if (withdrawAllowedStatuses.includes(this.request.currentStatus)) {
      // /\ above, checks if REQUEST allows action
      // \/ below, checks if YOU are allowed
      return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Withdraw);
    }
    return false;
  }

  restoreAllowed() {
    let purgedActionsStatuses: string[] = RequestStatuses.ThatCanBeRestored;
    if (purgedActionsStatuses.includes(this.request.currentStatus)) {
      // /\ above, checks if REQUEST allows action
      // \/ below, checks if YOU are allowed
      return this.verifyRequestActionService.IsAllowed(this.request, RequestActionTypeEnum.Restore);
    }
    return false;
  }

  goToRequestAllowed() {
    let goToRequestAllowedStatuses: string[] = RequestStatuses.ThatAreActorPending;
    let currentSendgroup = this.sendgroups.find(sg => sg.isActive && !sg.isCompleted);
    if (!currentSendgroup) {
      return false;
    }
    let hasActionInCurrentSendgroup = currentSendgroup.actors.findIndex(actor => MatchContacts(this.user, actor)) > -1;
    return goToRequestAllowedStatuses.includes(this.request.currentStatus) && hasActionInCurrentSendgroup;
  }

  public applyAction(action: RequestStatusActionsEnum) {
    switch (action) {
      case RequestStatusActionsEnum.Download:
        this.requestStatusService.downloadRequest(this.requestGuid);
        break;
      case RequestStatusActionsEnum.Export:
        this.requestStatusService.exportRequest(this.requestGuid, this.request.requestName);
        break;
      case RequestStatusActionsEnum.Purge:
        this.requestStatusService.purgeRequest(this.requestGuid);
        break;
      case RequestStatusActionsEnum.Delete:
        this.requestStatusService.openDeleteModal(this.request);
        break;
      case RequestStatusActionsEnum.Withdrawn:
        this.requestStatusService.openWithdrawModal(this.request);
        break;
      case RequestStatusActionsEnum.Restore:
        this.requestStatusService.restoreRequest(this.request);
        break;
      case RequestStatusActionsEnum.BecomeOwner:
        this.requestStatusService.becomeOwner(this.requestGuid);
        break;
      case RequestStatusActionsEnum.Viewlog:
        this.requestStatusService.openStatusViewLogModal(this.request);
        break;
      case RequestStatusActionsEnum.EditRequest:
        this.requestStatusService.openEditReqeustModal(this.request);
        break;
      case RequestStatusActionsEnum.GoToRequest:
        this.requestStatusService.goToRequest(this.request);
        break;
      default:
        break;
    }
  }
}

@Component({
    selector: 'desktop-status-actions',
    templateUrl: './desktop-status-actions.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DesktopStatusActions extends BaseStatusActions {
  constructor(protected override requestStatusService: RequestStatusService, protected override authenticationService: AuthenticationService, protected override verifyRequestActionService: VerifyRequestActionService) {
    super(requestStatusService, authenticationService, verifyRequestActionService);
  }
}

@Component({
    selector: 'mobile-status-actions',
    templateUrl: './mobile-status-actions.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MobileStatusActions extends BaseStatusActions {
  constructor(protected override requestStatusService: RequestStatusService, protected override authenticationService: AuthenticationService, protected override verifyRequestActionService: VerifyRequestActionService) {
    super(requestStatusService, authenticationService, verifyRequestActionService);
  }
}
